import dayjs from 'dayjs';
import styled from '@emotion/styled';
import React from 'react';
import { ApartmentIcon } from 'src/components/icons';
import { baseWidths, colors } from 'src/theme';
import { Flex } from 'src/components/grid';
import { Text } from 'src/components/typography';
import { Button } from 'src/components/buttons';
import { Accordion, Tag, Tooltip } from 'src/components/other';
import { Card } from 'src/components/cards';
import { DisplayDateFormat, i18n, resolvePremiums } from 'src/common';
import { IconInfo } from '@kompare/kompare-ui-library';
import { GetOfferDto } from 'src/common/api/apollo/types/types';

const isString = (x: unknown): x is string => typeof x === 'string';
const formatPrice = (n: number) => `${n.toFixed(2).replace('.', ',')}`;
const hasValue = (v: unknown) => v !== undefined && v !== null && v !== '';

const HeaderDataStyle = styled.div({
  display: 'flex',
  alignItems: 'center',
  background: colors.background7,
  flexDirection: 'column',
  padding: '10px',
});

const AccordionWrapper = styled(Flex)({
  width: '100%',
  ['@media (min-width: 484px)']: { width: baseWidths.mobile },
});

interface DetailRow {
  id?: string;
  label: string | React.ReactNode;
  value: string | React.ReactNode;
  disabled: boolean;
}

interface Props {
  offer: GetOfferDto;
  expanded: boolean | null;
  handleStyleExpansion: (value: boolean | null) => void;
}

export const HeaderDataFullDetails: React.FC<Props> = ({ offer, expanded, handleStyleExpansion }) => {
  const { productTypeSpecific: ps } = offer;
  const { payment, calculationData: calc, building, household, newPolicy } = ps;
  const perYear = i18n.t('layout.headerData.perYear');
  const perMonth = i18n.t('layout.headerData.perMonth');

  const { annual: yearlyPrice, monthly: monthlyPrice } = resolvePremiums(payment);
  const startDateFormatted = dayjs(newPolicy[0].newPolicyStartDate).format(DisplayDateFormat);

  const details: DetailRow[] = [
    {
      id: 'brandId',
      label: i18n.t('layout.headerData.insurance'),
      value: ps.brandId.name,
      disabled: !ps.brandId,
    },
    {
      id: 'packageName',
      label: i18n.t('layout.headerData.policy'),
      value: calc?.packageName ?? building?.package?.label ?? household?.package?.label,
      disabled: !calc?.packageName && !building?.package?.label && !household?.package?.label,
    },
    {
      id: 'productId',
      label: i18n.t('layout.headerData.included'),
      value: (
        <Flex vertical align='flex-end'>
          {hasValue(building?.package?.productId) && <Text type="small">{i18n.t('layout.headerData.buildingPart')}</Text>}
          {hasValue(household?.package?.productId) && <Text type="small">{i18n.t('layout.headerData.householdPart')}</Text>}
        </Flex>
      ),
      disabled: !hasValue(building?.package?.productId) && !hasValue(household?.package?.productId),
    },
    {
      id: 'additionalCoverages',
      label: i18n.t('layout.headerData.addOn'),
      value: calc?.additionalCoverages
        ? calc.additionalCoverages
            .filter((cov) => building.coverages?.find(bcov => bcov.code === cov.code) || household.coverages?.find(hcov => hcov.code === cov.code))
            .map((cov) => <Tag key={cov.code} value={cov.name} background={colors.success2} />)
        : [...(building.coverages ?? []), ...(household.coverages ?? [])]
            .map((cov) => <Tag key={cov.code} value={cov.label} background={colors.success2} />),
      disabled: (building.coverages!.length + household.coverages!.length) === 0,
    },
    {
      id: 'basePremium',
      label: i18n.t('layout.headerData.totalAmount'),
      value: `${formatPrice(yearlyPrice)} €/${perYear}`,
      disabled: !hasValue(yearlyPrice),
    },
    {
      id: 'discount',
      label: i18n.t('layout.headerData.discount'),
      value: `-${formatPrice(payment.discount!)}`,
      disabled: !hasValue(payment?.discount) || payment.discount === 0,
    },
    {
      id: 'totalPremium',
      label: i18n.t('layout.headerData.toPay'),
      value: (
        <Flex gap={8} align="flex-end" wrap="wrap">
          <Text type="small" bold>{`${formatPrice(payment.totalPremium!)} €/${perYear}`}</Text>
          <Text type="small">{`${formatPrice(monthlyPrice)} €/${perMonth}`}</Text>
        </Flex>
      ),
      disabled: !hasValue(payment?.totalPremium),
    },
    {
      id: 'newPolicyStartDate',
      label: (
        <Flex gap={8} align="center">
          <Text type="small">{i18n.t('layout.headerData.startDate')}</Text>
          <Tooltip placement='bottom' content={<Text type="small" italic>Valjani početak police naveden je u samom dokumentu police.</Text>}>
            <IconInfo width={12} height={12} />
          </Tooltip>
        </Flex>
      ),
      value: startDateFormatted,
      disabled: !startDateFormatted,
    },
  ];

  return (
    <HeaderDataStyle>
      <AccordionWrapper justify='stretch'>
        <Accordion
          title={(
            <Flex justify="space-between">
              <Flex gap={16} align="center" justify="space-between">
                <ApartmentIcon color={colors.primary} />
                <Flex vertical align="flex-start">
                  <Text type="medium" bold>{i18n.t('layout.headerData.propertyInsurance')}</Text>
                  <Text type="small">{`${formatPrice(yearlyPrice)} €/${perYear} (${formatPrice(monthlyPrice)} €/${perMonth})`}</Text>
                  {startDateFormatted && (
                    <Text color={colors.text3} type="tiny">{i18n.t('layout.headerData.startAt', { date: startDateFormatted })}</Text>
                  )}
                </Flex>
              </Flex>
              <Button
                onClick={() => handleStyleExpansion(!expanded)}
                text={expanded ? i18n.t('layout.headerData.showLess') : i18n.t('layout.headerData.details')}
                size='small'
                btnType='textPrimary'
              />
            </Flex>
          )}
          content={(
            <Flex vertical gap={2}>
              {details.filter((d) => !d.disabled).map((detail) => (
                <Card
                  key={detail.id}
                  style={{
                    padding: '8px 8px',
                    backgroundColor: colors.background6,
                    borderRadius: '4px 4px 0 0',
                    opacity: detail.disabled ? 0.6 : 1,
                  }}
                >
                  <Flex gap={8} justify="space-between" align="center">
                    {isString(detail.label) ? (
                      <Text type="small">{detail.label}</Text>
                    ) : (
                      detail.label
                    )}
                    <Flex gap={8} align="flex-end" justify="flex-end" wrap="wrap">
                      {isString(detail.value) ? (
                        <Text type="small">{detail.value}</Text>
                      ) : (
                        detail.value
                      )}
                    </Flex>
                  </Flex>
                </Card>
              ))}
            </Flex>
          )}
          gap={12}
          onChange={() => handleStyleExpansion(!expanded)}
          expandIconComponent={<></>}
        />
      </AccordionWrapper>
    </HeaderDataStyle>
  );
};
